import { TableCell, TableFooter, TableRow, Tooltip } from "@mui/material";
import type { ReactNode } from "react";
import type { GasEntryMeter, GasEntryTarget } from "../models/GasEntry";
import type { GasEntryMonthTotals } from "../utils/gasEntryRows";
import { CellValue } from "./CellValue";

const format = (value: number | null, digits: number) =>
	value === null
		? ""
		: value.toLocaleString("en-US", { maximumFractionDigits: digits });

const TotalCell = ({
	title,
	testId,
	colSpan,
	align = "right",
	children,
}: {
	title?: ReactNode;
	testId?: string;
	colSpan?: number;
	align?: "right" | "center";
	children?: ReactNode;
}) => {
	const cell = (
		<TableCell
			colSpan={colSpan}
			data-test-id={testId}
			sx={{
				backgroundColor: "#EEF1F4",
				borderTop: "2px solid",
				borderTopColor: "divider",
				bottom: 0,
				color: "text.primary",
				fontWeight: 600,
				position: "sticky",
				zIndex: 2,
				textAlign: align,
			}}
		>
			<CellValue>{children}</CellValue>
		</TableCell>
	);
	return title === undefined ? cell : <Tooltip title={title}>{cell}</Tooltip>;
};

interface GasEntryMonthTotalRowProps {
	meters: GasEntryMeter[];
	targets: GasEntryTarget[];
	totals: GasEntryMonthTotals;
}

export const GasEntryMonthTotalRow = ({
	meters,
	targets,
	totals,
}: GasEntryMonthTotalRowProps) => (
	<TableFooter>
		<TableRow data-test-id="gas-entry-month-total">
			<TotalCell align="center">Month</TotalCell>
			{meters.flatMap((meter) => [
				<TotalCell
					key={`uv-${meter.GasMeterId}`}
					testId={`total-uv-${meter.MeterCode}`}
					title={`${meter.MeterName}: uncorrected volume read this month.`}
				>
					{format(totals.uncorrectedVolumeByMeterId[meter.GasMeterId], 0)}
				</TotalCell>,
				<TotalCell
					key={`mcf-${meter.GasMeterId}`}
					testId={`total-mcf-${meter.MeterCode}`}
					colSpan={2}
					title={`${meter.MeterName}: this month's readings corrected to base conditions.`}
				>
					{totals.mcfByMeterId[meter.GasMeterId] === null
						? ""
						: `${format(totals.mcfByMeterId[meter.GasMeterId], 1)} MCF`}
				</TotalCell>,
			])}
			<TotalCell
				testId="total-heat-value"
				title="Average heat value of the month, weighted by each day's MCF."
			>
				{format(totals.heatValueBtuPerCf, 2)}
			</TotalCell>
			<TotalCell testId="total-month-mcf">
				{format(totals.totalMcf, 1)}
			</TotalCell>
			<TotalCell testId="total-month-mmbtu">
				{format(totals.totalMmbtu, 1)}
			</TotalCell>
			{targets.map((target) => (
				<TotalCell
					key={`pct-${target.TargetId}`}
					testId={`total-share-${target.TargetCode}`}
					title={`${target.TargetName}: share of the month's MCF so far.`}
				>
					{format(totals.percentByTargetId[target.TargetId], 2)}
				</TotalCell>
			))}
			{targets.map((target) => (
				<TotalCell
					key={`brk-${target.TargetId}`}
					testId={`total-breakdown-${target.TargetCode}`}
				>
					{format(totals.breakdownByTargetId[target.TargetId], 1)}
				</TotalCell>
			))}
			<TotalCell />
		</TableRow>
	</TableFooter>
);
